import { useMemo } from 'react';
import { parseDate, isValidDate, calculateSLAStatus, formatPriority } from '../../../utils/slaDashboardUtils';
import { AT_RISK_HOURS_THRESHOLD } from '../../../constants/slaDashboard';

/**
 * Hook to calculate SLA adherence by priority
 * @param {Array} queries - Array of queries
 * @param {Date} now - Current date
 * @returns {Array} SLA data by priority
 */
export const useSLAByPriority = (queries, now) => {
  return useMemo(() => {
    const priorityMap = new Map();

    queries.forEach((query) => {
      const due = parseDate(query.slaDueDate);
      // Only process queries with SLA due dates
      if (!due || !isValidDate(due)) {
        return;
      }

      const priority = formatPriority(query.priority);
      if (!priorityMap.has(priority)) {
        priorityMap.set(priority, { total: 0, within: 0, breached: 0 });
      }

      const entry = priorityMap.get(priority);
      entry.total += 1;

      const respondedAt = parseDate(query.respondedAt);
      const closedAt = parseDate(query.closedAt);
      const completedAt = respondedAt || closedAt;

      const status = calculateSLAStatus(due, completedAt, now, AT_RISK_HOURS_THRESHOLD);

      if (status === 'Breached') {
        entry.breached += 1;
      } else {
        entry.within += 1;
      }

      priorityMap.set(priority, entry);
    });

    return Array.from(priorityMap.entries()).map(([priority, data]) => ({
      priority,
      adherence: data.total > 0 ? Number(((data.within / data.total) * 100).toFixed(1)) : 0,
      total: data.total,
      breached: data.breached,
    }));
  }, [queries, now]);
};
